// ============================================================================
// Badge Component
// Feature: 099-app-review-optimization
// Created: 2026-01-11
// Description: Base badge component for status indicators
// ============================================================================

import { forwardRef } from 'react';
import type { HTMLAttributes, ReactNode } from 'react';
import { clsx } from 'clsx';

/**
 * Badge variants
 */
export type BadgeVariant =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'error'
  | 'danger' // Alias for error
  | 'info';

/**
 * Badge sizes
 */
export type BadgeSize = 'xs' | 'sm' | 'md' | 'lg';

/**
 * Capacity type badge variants
 */
export type CapacityBadgeVariant = 'Platform' | 'Reference' | 'ClientDedicated' | 'MultiTenant';

/**
 * Badge props
 */
export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  size?: BadgeSize;
  isDot?: boolean;
  isOutlined?: boolean;
  isPill?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  children?: ReactNode;
}

/**
 * Base styles for all badges
 */
const baseStyles = 'inline-flex items-center font-medium';

/**
 * Variant styles (solid)
 */
const variantStyles: Record<BadgeVariant, string> = {
  default: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  primary: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  secondary: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
  success: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  error: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  danger: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300', // Alias for error
  info: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300',
};

/**
 * Outlined variant styles
 */
const outlinedVariantStyles: Record<BadgeVariant, string> = {
  default: 'border border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-300',
  primary: 'border border-blue-500 text-blue-700 dark:border-blue-400 dark:text-blue-300',
  secondary: 'border border-gray-400 text-gray-600 dark:border-gray-500 dark:text-gray-400',
  success: 'border border-green-500 text-green-700 dark:border-green-400 dark:text-green-300',
  warning: 'border border-yellow-500 text-yellow-700 dark:border-yellow-400 dark:text-yellow-300',
  error: 'border border-red-500 text-red-700 dark:border-red-400 dark:text-red-300',
  danger: 'border border-red-500 text-red-700 dark:border-red-400 dark:text-red-300', // Alias for error
  info: 'border border-cyan-500 text-cyan-700 dark:border-cyan-400 dark:text-cyan-300',
};

/**
 * Size styles
 */
const sizeStyles: Record<BadgeSize, string> = {
  xs: 'text-xs px-1.5 py-0.5 gap-1',
  sm: 'text-xs px-2 py-0.5 gap-1',
  md: 'text-sm px-2.5 py-0.5 gap-1.5',
  lg: 'text-sm px-3 py-1 gap-2',
};

/**
 * Dot sizes
 */
const dotSizes: Record<BadgeSize, string> = {
  xs: 'h-1.5 w-1.5',
  sm: 'h-2 w-2',
  md: 'h-2.5 w-2.5',
  lg: 'h-3 w-3',
};

/**
 * Badge Component
 */
export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      variant = 'default',
      size = 'sm',
      isDot = false,
      isOutlined = false,
      isPill = true,
      leftIcon,
      rightIcon,
      children,
      className,
      ...props
    },
    ref
  ) => {
    const effectiveVariantStyles = isOutlined
      ? outlinedVariantStyles[variant]
      : variantStyles[variant];

    return (
      <span
        ref={ref}
        className={clsx(
          baseStyles,
          effectiveVariantStyles,
          sizeStyles[size],
          isPill ? 'rounded-full' : 'rounded',
          className
        )}
        {...props}
      >
        {isDot && (
          <span
            className={clsx(
              'rounded-full',
              dotSizes[size],
              variant === 'success' && 'bg-green-500',
              variant === 'warning' && 'bg-yellow-500',
              variant === 'error' && 'bg-red-500',
              variant === 'info' && 'bg-cyan-500',
              variant === 'primary' && 'bg-blue-500',
              variant === 'secondary' && 'bg-gray-500',
              variant === 'default' && 'bg-gray-400'
            )}
          />
        )}
        {leftIcon}
        {children}
        {rightIcon}
      </span>
    );
  }
);

Badge.displayName = 'Badge';

/**
 * Capacity type badge styles
 */
const capacityBadgeStyles: Record<CapacityBadgeVariant, string> = {
  Platform: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
  Reference: 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300',
  ClientDedicated: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
  MultiTenant: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
};

/**
 * Capacity Badge Component
 */
export interface CapacityBadgeProps extends Omit<BadgeProps, 'variant'> {
  capacityType: CapacityBadgeVariant;
}

export const CapacityBadge = forwardRef<HTMLSpanElement, CapacityBadgeProps>(
  ({ capacityType, size = 'sm', className, children, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={clsx(
          baseStyles,
          capacityBadgeStyles[capacityType],
          sizeStyles[size],
          'rounded-full',
          className
        )}
        {...props}
      >
        {children || capacityType}
      </span>
    );
  }
);

CapacityBadge.displayName = 'CapacityBadge';

/**
 * Status Badge Component
 */
export type StatusType = 'active' | 'inactive' | 'pending' | 'completed' | 'failed' | 'unknown';

export interface StatusBadgeProps extends Omit<BadgeProps, 'variant'> {
  status: StatusType;
}

const statusToVariant: Record<StatusType, BadgeVariant> = {
  active: 'success',
  inactive: 'secondary',
  pending: 'warning',
  completed: 'primary',
  failed: 'error',
  unknown: 'default',
};

export const StatusBadge = forwardRef<HTMLSpanElement, StatusBadgeProps>(
  ({ status, children, ...props }, ref) => {
    return (
      <Badge ref={ref} variant={statusToVariant[status]} isDot {...props}>
        {children || status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  }
);

StatusBadge.displayName = 'StatusBadge';

export default Badge;
